import { Link } from "react-router-dom";

/**
 * Link que elige solo entre `<Link>` y `<a>`.
 *
 * POR QUÉ EXISTE: el sitio son DOS apps detrás del mismo balanceador — esta SPA
 * (montada en `/app/...`) y la app Next (que sirve `/{país}/...`). Los menús
 * mezclan destinos de las dos.
 *
 * Con `<Link>` de react-router, el click NO hace request al servidor: resuelve la
 * ruta en el cliente. Si el destino lo sirve la Next, la SPA no tiene esa ruta en
 * su router → renderiza su 404 sin que el balanceador llegue a enterarse. Por eso
 * todo lo que salga de la SPA tiene que ser un `<a>` real: fuerza la navegación
 * del browser y ahí sí el balanceador rutea a la app correcta.
 *
 * Criterio: sólo `/app/...` es de esta SPA. Todo lo demás (incluido `/{país}/...`
 * y las URLs absolutas) sale como `<a>`.
 */

/** ¿La ruta la resuelve el router de ESTA SPA? Sólo el árbol `/app/...`. */
export const isSpaRoute = (href?: string): boolean =>
  typeof href === "string" && href.startsWith("/app/");

interface CrossAppLinkProps {
  to?: string;
  className?: string;
  onClick?: () => void;
  target?: string;
  rel?: string;
  children: React.ReactNode;
  [key: string]: any;
}

const CrossAppLink = ({ to, children, ...rest }: CrossAppLinkProps) => {
  // Sin destino no se renderiza un ancla rota: se devuelve el contenido pelado.
  if (!to) return <>{children}</>;

  if (isSpaRoute(to)) {
    return (
      <Link to={to} {...rest}>
        {children}
      </Link>
    );
  }

  // Externo a la SPA (Next o dominio ajeno) → navegación real del browser.
  return (
    <a href={to} {...rest}>
      {children}
    </a>
  );
};

export default CrossAppLink;
